import type { ComponentProps } from 'react'

import { iconButtonClass, type IconButtonVariant } from '@/components/ui/icon-button-variants'

interface IconButtonProps extends ComponentProps<'button'> {
  /** Required: the glyph is the only label this control has. */
  'aria-label': string
  /**
   * `ghost` and `outline` are the round chrome buttons. `toolbar` is the
   * square one for a control row of square tabs and segments, like the chart
   * type button beside Line / Candles (`4236:100828`). `square` is the dialog
   * close, a 32px square on the inset grey (`6042:241`), which Modal and
   * PeriodPicker share. `muted` is that same 32px on the same grey, round:
   * the top nav's search.
   */
  variant?: IconButtonVariant
  /** Unread count. Shows an accent dot; the number is read out, not drawn. */
  badge?: number
}

/**
 * The icon-only control: round in the chrome and card headers (the nav's menu
 * and search, a row's ⋮), square in a toolbar and as a dialog's close ×.
 *
 * Round is 36px by default, Figma's 34 on the 4-grid; a caller that needs
 * another size passes it through `className`. The toolbar square is 40, the
 * dialog close and the round `muted` search 32.
 *
 * Both are below the 44px touch minimum, so below `lg` the hit area is grown
 * past the visual bounds by a transparent `::before` rather than by making the
 * button bigger. Growing it would reflow the header and card rows it sits in
 * at exactly the width where there is least room; an extended hit area costs
 * no layout and is what both HIG and Material ask for when a control has to
 * stay visually small. It is `TOUCH_TARGET`, so the target is 44 at whatever
 * size a caller draws the button.
 */
export default function IconButton({
  variant = 'ghost',
  badge,
  className,
  children,
  ...props
}: IconButtonProps) {
  const unread = badge !== undefined && badge > 0

  return (
    <button type="button" className={iconButtonClass(variant, className)} {...props}>
      {children}
      {unread ? (
        <>
          <span
            className="absolute top-0 right-0 size-2 rounded-full bg-accent"
            aria-hidden="true"
          />
          <span className="sr-only">{badge} unread</span>
        </>
      ) : null}
    </button>
  )
}
